import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { mapStrings, toSimplified } from '@lc/contracts';
import { setApiLanguage } from './api';

/** The site's languages: Traditional Chinese, Simplified (made from the Traditional) and English. */
export type Lang = 'zh-HK' | 'zh-CN' | 'en';
const LANGS: readonly Lang[] = ['zh-HK', 'zh-CN', 'en'];
/** Copy written in the code: Traditional Chinese and English (Simplified is made from the Traditional). */
export type Text = Record<'zh-HK' | 'en', string>;

const zh = {
  'nav.home': '首頁',
  'nav.shop': '商店',
  'nav.account': '帳戶',
  'nav.login': '登入',
  'nav.prereg': '預約',
  'footer.privacy': '私隱政策',
  'footer.terms': '服務條款',
  'footer.copy': '© 落筆成戀 Inked in Love',

  'landing.title1': '搬進社區，',
  'landing.title2': '你的選擇會變成漫畫',
  'landing.lead1': '在墨染町認識鄰居、談一場戀愛，',
  'landing.lead2': '每個完成的故事都畫成屬於你的漫畫。',
  'landing.appStore': 'App Store 下載',
  'landing.appStoreSoon': '即將登陸 App Store',
  'landing.toShop': '前往商店',
  'landing.f1.title': '你的選擇，畫成漫畫',
  'landing.f1.body': '你的每個選擇都會影響故事，完成後畫成屬於你的漫畫。',
  'landing.f2.title': '十六位鄰居，\n各有故事',
  'landing.f2.body': '性格各異的鄰居，各自的日常與秘密，走出只屬於你的關係。',
  'landing.f3.title': '黑白週刊\n漫畫精繪版',
  'landing.f3.body': '把喜歡的章節畫成黑白週刊漫畫，分鏡細緻，收藏你的故事。',

  'login.title': '登入',
  'login.step1': '輸入電郵地址',
  'login.email': '電郵地址',
  'login.send': '傳送驗證碼',
  'login.step2': '輸入 6 位數驗證碼',
  'login.sentTo': '驗證碼已傳送到 {email}',
  'login.submit': '登入',
  'login.resend': '重新傳送',
  'login.noCode': '沒有收到？',
  'login.changeEmail': '更改電郵',
  'login.same': '與 App 使用同一個帳戶',
  'login.badEmail': '請輸入有效的電郵地址。',
  'login.wrongCode': '驗證碼不正確或已過期，請再試一次。',

  'shop.title': '墨店',
  'shop.plus': '會員',
  'shop.credits': '章節券',
  'shop.other': '墨染月卡',
  'shop.balance': '章節券',
  'shop.currency': '貨幣',
  'shop.buy': '購買',
  'shop.subscribe': '訂閱',
  'shop.owned': '已擁有',
  'shop.subscribed': '已訂閱',
  'shop.unavailable': '暫不可購買',
  'shop.per.month': '／月',
  'shop.secure': '以 Stripe 安全付款・章節券即時加入你的帳戶',
  'shop.loginFirst': '登入後即可購買，章節券會加入你在 App 使用的帳戶。',
  'shop.cancelled': '付款已取消，你沒有被收取任何費用。',
  'shop.unit.credits': '話',
  'shop.perChapter': '每話 {price}',
  'shop.creditsNote': '每張章節券換一話 8 頁精繪漫畫，永不過期。',
  'badge.popular': '最受歡迎',
  'badge.best_value': '最划算',
  'badge.limited': '限時',

  'account.title': '帳戶',
  'account.credits': '章節券',
  'account.coins': '墨幣',
  'account.plus': 'Inked Plus',
  'account.plusOff': '尚未訂閱 Inked Plus',
  'account.renews': '下次續訂 {date}',
  'account.manage': '管理訂閱',
  'account.manageAppStore': '這個 Plus 是在 App Store 訂閱的：請在 iPhone「設定 → Apple 帳戶 → 訂閱」管理或取消。',
  'account.doubleBilled': '你同時有兩個 Plus 訂閱（App Store 和官網），兩邊都會收費。請取消其中一個：官網的在上面「管理訂閱」，App Store 的在 iPhone「設定 → Apple 帳戶 → 訂閱」。',
  'account.history': '購買紀錄',
  'account.empty': '還沒有購買紀錄。',
  'account.date': '日期',
  'account.item': '項目',
  'account.amount': '章節券',
  'account.logout': '登出',
  'account.test': '測試',

  'handoff.signingIn': '正在登入你的帳戶…',
  'handoff.failed': '連結已失效',
  'handoff.failedBody': '這個連結只能使用一次，並在一分鐘後失效。請從 App 再開一次，或用電郵登入。',

  'success.title': '付款成功',
  'success.body': '感謝你的支持！章節券和會員權益會在數秒內加入你的帳戶，打開 App 就會看到。',
  'success.toAccount': '查看帳戶',
  'success.toShop': '返回商店',

  'error.generic': '出了點問題，請稍後再試。',
  'landing.cast': '墨染町的六位主角',
  'login.tagline': '漫畫 × 戀愛 × 選擇',
  'plan.plus': 'Inked Plus',
  'plan.premium': 'Inked Premium',
  'shop.currentPlan': '目前方案',
  'shop.changePlan': '更改方案',
  'account.tagline': '戀愛 × 漫畫 × 創作遊戲',
  'account.renewsOn': '下次續訂',
  'account.status': '狀態',
  'account.done': '已完成',
  'account.refunded': '已退款',
  'plan.short.tickets': '每月 {n} 話精繪漫畫',
  'plan.short.coins': '每日 {n} 墨幣',
  'plan.short.chats': '每日聊天 {n} 句、{c} 章故事',
  'shop.more': '了解更多',
  'shop.less': '收起',
  'shop.gift.title': '預約禮物：一次購買減 {n}%',
  'shop.gift.body': '勾選後下一次購買自動扣減，月卡、會員首月和章節券都適用，只限一次。',
  'shop.gift.firstMonth': '首月價，之後按原價續訂',
  'account.paid': '金額',
  'loading': '載入中…',
  'share.loading': '漫畫翻開中……',
  'share.language': '語言',
  'share.languageHint': '只會轉換按鈕和提示，漫畫內容維持分享者的語言。',
  'share.end.prereg': '預約，上線時通知我',
  'prereg.title': '預約落筆成戀',
  'prereg.body': '留下電郵，遊戲上線時第一時間通知你，並送上預約禮物：官網商店一次購買半價。',
  'prereg.email': '你的電郵地址',
  'prereg.submit': '預約',
  'prereg.note': '只用來通知你上線和送出禮物，不會用作其他用途。',
  'prereg.error': '暫時未能登記，請稍後再試。',
  'prereg.done.title': '預約成功！',
  'prereg.done.body': '確認電郵已經寄出，遊戲上線時會再以電郵通知你。如果你早前已經預約過，就不會再收到確認電郵。用同一個電郵登入官網商店，就能用半價禮物。',
  'prereg.pageTitle': '搶先預約',
  'prereg.pageLead': '搬進墨染町，你的每個選擇都會畫成只屬於你的漫畫。現在預約，上線時第一時間通知你，還送你官網商店一次購買半價。',
  'legal.failed': '暫時載入不到這一頁，請稍後再試。',
  'share.gone.title': '這個分享已經關閉',
  'share.gone.body': '分享的人可能已經收回這一話，或者連結不完整。',
  'share.gone.home': '看看落筆成戀',
  'share.error': '暫時載入不到這一話。',
  'share.retry': '再試一次',
  'share.chapterNo': '第 {n} 話',
  'share.day': '第 {day} 天',
  'share.illustrated': '精繪版',
  'share.pages': '共 {n} 頁',
  'share.start': '開始閱讀',
  'share.swipeLtr': '向左滑，或點畫面右邊翻頁。點中間可以收起工具列。',
  'share.keys': '也可以用鍵盤的 ← → 翻頁。',
  'share.swipeRtl': '向右滑，或點畫面左邊翻頁。點中間可以收起工具列。',
  'share.pageOf': '{n} / {total}',
  'share.goPage': '跳到第 {n} 頁',
  'share.settings': '閱讀設定',
  'share.mode': '閱讀方式',
  'share.mode.paged': '翻頁',
  'share.mode.scroll': '捲動',
  'share.direction': '翻頁方向',
  'share.ltr': '左至右',
  'share.rtl': '右至左',
  'share.ltrHint': '向左滑看下一頁，跟一般網頁一樣。',
  'share.rtlHint': '向右滑看下一頁，跟日本漫畫單行本一樣。',
  'share.close': '關閉',
  'share.end.title': '完',
  'share.end.body': '《{title}》是由一位玩家的選擇畫出來的。',
  'share.end.cta': '玩出你自己的漫畫',
  'share.end.ctaBody': '搬進墨染町認識鄰居，你的每個選擇都會畫成只屬於你的漫畫。',
  'share.end.again': '從頭再看',
};

const en: Record<keyof typeof zh, string> = {
  'nav.home': 'Home',
  'nav.shop': 'Shop',
  'nav.account': 'Account',
  'nav.login': 'Sign in',
  'nav.prereg': 'Pre-register',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'footer.copy': '© Inked in Love',

  'landing.title1': 'Fall for the neighbours.',
  'landing.title2': 'Watch it become manga.',
  'landing.lead1': 'Settle into Inkwell, get to know the people next door, and maybe fall for one of them.',
  'landing.lead2': 'Every chapter you finish comes back as a manga that’s yours alone.',
  'landing.appStore': 'Download on the App Store',
  'landing.appStoreSoon': 'Coming soon to iPhone',
  'landing.toShop': 'Shop now',
  'landing.f1.title': 'You choose,\nwe draw',
  'landing.f1.body': 'Every reply, every date, every moment you hold back shapes the story — and each chapter you finish comes back as manga.',
  'landing.f2.title': 'Sixteen neighbours,\nsixteen stories',
  'landing.f2.body': 'Each with a life, a past and a secret of their own. Who you get close to is up to you.',
  'landing.f3.title': 'Your chapter,\nfully illustrated',
  'landing.f3.body': 'Turn a favourite chapter into a black-and-white manga, drawn page by page like a real weekly serial.',

  'login.title': 'Sign in',
  'login.step1': 'Your email',
  'login.email': 'Email address',
  'login.send': 'Send code',
  'login.step2': 'Enter your 6-digit code',
  'login.sentTo': 'Code sent to {email}',
  'login.submit': 'Sign in',
  'login.resend': 'Resend',
  'login.noCode': "Didn't get it?",
  'login.changeEmail': 'Change email',
  'login.same': 'Uses the same account as the app',
  'login.badEmail': 'Please enter a valid email address.',
  'login.wrongCode': 'That code didn’t work or has expired. Try again.',

  'shop.title': 'Ink Shop',
  'shop.plus': 'Membership',
  'shop.credits': 'Chapter tickets',
  'shop.other': 'Monthly Pass',
  'shop.balance': 'Tickets',
  'shop.currency': 'Currency',
  'shop.buy': 'Buy',
  'shop.subscribe': 'Subscribe',
  'shop.owned': 'Owned',
  'shop.subscribed': 'Subscribed',
  'shop.unavailable': 'Unavailable',
  'shop.per.month': '/month',
  'shop.secure': 'Secure checkout with Stripe · tickets land in your account instantly',
  'shop.loginFirst': 'Sign in to buy — tickets go straight to your game account.',
  'shop.cancelled': 'Payment cancelled — you haven’t been charged.',
  'shop.unit.credits': 'chapters',
  'shop.perChapter': '{price} each',
  'shop.creditsNote': 'Each ticket is an 8-page full chapter. Tickets never expire.',
  'badge.popular': 'Most popular',
  'badge.best_value': 'Best value',
  'badge.limited': 'Limited',

  'account.title': 'Account',
  'account.credits': 'Chapter tickets',
  'account.coins': 'Ink coins',
  'account.plus': 'Inked Plus',
  'account.plusOff': 'No Inked Plus subscription',
  'account.renews': 'Renews {date}',
  'account.manage': 'Manage subscription',
  'account.manageAppStore': 'This Plus subscription is through the App Store. Manage or cancel it on your iPhone in Settings → your Apple Account → Subscriptions.',
  'account.doubleBilled': "You have two Inked Plus subscriptions, one through the App Store and one here, and both are charging you. Cancel one: this one with Manage subscription above, the App Store one on your iPhone in Settings → your Apple Account → Subscriptions.",
  'account.history': 'Purchases',
  'account.empty': 'No purchases yet.',
  'account.date': 'Date',
  'account.item': 'Item',
  'account.amount': 'Tickets',
  'account.logout': 'Sign out',
  'account.test': 'Test',

  'handoff.signingIn': 'Signing you in…',
  'handoff.failed': 'This link has expired',
  'handoff.failedBody': 'App links work once and expire after a minute. Open it from the app again, or sign in with your email.',

  'success.title': 'Payment complete',
  'success.body': 'Thanks for your support! Your tickets and perks will show up in your account within a few seconds — open the app to check.',
  'success.toAccount': 'View account',
  'success.toShop': 'Back to shop',

  'error.generic': 'Something went wrong. Please try again later.',
  'landing.cast': 'Inkwell’s six leads',
  'login.tagline': 'Manga × Romance × Choice',
  'plan.plus': 'Inked Plus',
  'plan.premium': 'Inked Premium',
  'shop.currentPlan': 'Current plan',
  'shop.changePlan': 'Change plan',
  'account.tagline': 'Romance × Manga × You',
  'account.renewsOn': 'Renews',
  'account.status': 'Status',
  'account.done': 'Complete',
  'account.refunded': 'Refunded',
  'plan.short.tickets': '{n} full chapters a month',
  'plan.short.coins': '{n} ink coins a day',
  'plan.short.chats': '{n} chats and {c} chapters a day',
  'shop.more': 'Details',
  'shop.less': 'Hide',
  'shop.gift.title': 'Your pre-registration gift: {n}% off one purchase',
  'shop.gift.body': 'Leave this ticked and it comes off your next purchase: the monthly pass, a membership’s first month or a ticket pack. One use only.',
  'shop.gift.firstMonth': 'First month, then the regular price',
  'account.paid': 'Amount',
  'loading': 'Loading…',
  'share.loading': 'Opening the manga…',
  'share.language': 'Language',
  'share.languageHint': 'Changes the buttons and tips. The manga stays in the language it was shared in.',
  'share.end.prereg': 'Pre-register to hear first',
  'prereg.title': 'Pre-register for Inked in Love',
  'prereg.body': 'Leave your email and we’ll tell you the moment the game launches, plus half off one purchase in our web shop for signing up early.',
  'prereg.email': 'Your email',
  'prereg.submit': 'Pre-register',
  'prereg.note': 'We’ll only use it to tell you about the launch and send your gift.',
  'prereg.error': 'We couldn’t sign you up just now. Please try again in a moment.',
  'prereg.done.title': 'You’re on the list!',
  'prereg.done.body': 'Check your inbox for a confirmation, and we’ll email you again when the game launches. If you’d already signed up, there’s no second confirmation. Sign in to the web shop with this email to use your half-price gift.',
  'prereg.pageTitle': 'Pre-register',
  'prereg.pageLead': 'Move into Inkwell, where every choice you make becomes a manga that’s yours alone. Pre-register now to hear first when it launches, and get half off one purchase in our web shop.',
  'legal.failed': 'We couldn’t load this page. Please try again in a moment.',
  'share.gone.title': 'This share has been closed',
  'share.gone.body': 'Whoever shared this chapter may have taken it back, or the link is incomplete.',
  'share.gone.home': 'Discover Inked in Love',
  'share.error': 'We couldn’t load this chapter right now.',
  'share.retry': 'Try again',
  'share.chapterNo': 'Chapter {n}',
  'share.day': 'Day {day}',
  'share.illustrated': 'Illustrated',
  'share.pages': '{n} pages',
  'share.start': 'Start reading',
  'share.swipeLtr': 'Swipe left or tap the right edge to turn the page. Tap the middle to hide the bars.',
  'share.keys': 'The ← → keys turn pages too.',
  'share.swipeRtl': 'Swipe right or tap the left edge to turn the page. Tap the middle to hide the bars.',
  'share.pageOf': '{n} / {total}',
  'share.goPage': 'Go to page {n}',
  'share.settings': 'Reading settings',
  'share.mode': 'Layout',
  'share.mode.paged': 'Pages',
  'share.mode.scroll': 'Scroll',
  'share.direction': 'Page turns',
  'share.ltr': 'Left to right',
  'share.rtl': 'Right to left',
  'share.ltrHint': 'Swipe left for the next page, like most sites.',
  'share.rtlHint': 'Swipe right for the next page, the way Japanese manga books read.',
  'share.close': 'Close',
  'share.end.title': 'END',
  'share.end.body': '“{title}” was drawn from one player’s choices.',
  'share.end.cta': 'Play your own manga',
  'share.end.ctaBody': 'Move into Inkwell, meet the neighbours, and every choice you make becomes a manga that’s yours alone.',
  'share.end.again': 'Read it again',
};

export type Key = keyof typeof zh;
/** Only a language the visitor picked is remembered (the old key saved the browser's guess too). */
const LANG_KEY = 'inked.web.lang.picked';
/**
 * The language the visitor picked, else the Chinese their browser asks for (Simplified for zh-CN,
 * zh-SG, zh-Hans; Traditional for any other zh). Everyone else starts in Traditional: many Hong Kong
 * and Taiwan phones run in English, and those players still read Chinese first.
 */
const initial = (): Lang => {
  try {
    const picked = localStorage.getItem(LANG_KEY) as Lang | null;
    if (picked && LANGS.includes(picked)) return picked;
  } catch {
    // ignore
  }
  const asked = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];
  const chinese = asked.map((l) => l.toLowerCase()).find((l) => l === 'zh' || l.startsWith('zh-'));
  return chinese && /^zh-(cn|sg|my|hans)/.test(chinese) ? 'zh-CN' : 'zh-HK';
};

let zhCN: typeof zh | null = null;
const dictOf = (lang: Lang): typeof zh => (lang === 'en' ? en : lang === 'zh-CN' ? (zhCN ??= mapStrings(zh, toSimplified)) : zh);
const HTML_LANG: Record<Lang, string> = { 'zh-HK': 'zh-Hant-HK', 'zh-CN': 'zh-Hans-CN', en: 'en' };

interface I18n {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: Key, vars?: Record<string, string | number>) => string;
  /** Copy written in the code, in the visitor's language. */
  tx: (text: Text) => string;
}
const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initial);
  setApiLanguage(lang);
  useEffect(() => {
    document.documentElement.lang = HTML_LANG[lang];
  }, [lang]);
  const value = useMemo<I18n>(() => {
    const setLang = (l: Lang) => {
      setLangState(l);
      try {
        localStorage.setItem(LANG_KEY, l);
      } catch {
        // ignore
      }
    };
    const dict = dictOf(lang);
    const tx = (text: Text) => (lang === 'en' ? text.en : lang === 'zh-CN' ? toSimplified(text['zh-HK']) : text['zh-HK']);
    return { lang, setLang, tx, t: (key, vars) => Object.entries(vars ?? {}).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), dict[key]) };
  }, [lang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}
